import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import type {
  CatalogueEntrySummary,
  CatalogueTemplateGroup,
} from "@/services/api/guardiansv2/types";
import { catalogueEntryReactKey } from "./catalogueEntryKey";

export interface RulesTaskTypeSidebarProps {
  groups: CatalogueTemplateGroup[];
  ungrouped: CatalogueEntrySummary[];
  catalogueKey: string | null;
  onSelect: (key: string) => void;
}

function TaskTypeButton({
  entry,
  selected,
  onSelect,
}: {
  entry: CatalogueEntrySummary;
  selected: boolean;
  onSelect: (key: string) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(entry.key)}
      className={
        selected
          ? "w-full rounded-md bg-indigo-600/15 py-1 text-left text-sm font-normal text-indigo-200"
          : "w-full rounded-md py-1 text-left text-sm font-normal text-slate-400 hover:bg-slate-800/80 hover:text-slate-200"
      }
      data-testid={`rules-task-type-${entry.key}`}
    >
      {entry.display_name}
    </button>
  );
}

function TaskTypeGroup({
  group,
  catalogueKey,
  onSelect,
  testId,
}: {
  group: CatalogueTemplateGroup;
  catalogueKey: string | null;
  onSelect: (key: string) => void;
  testId?: string;
}) {
  return (
    <details
      className="group/rules-catalogue"
      data-testid={testId ?? `rules-catalogue-group-${group.template_id}`}
    >
      <summary className="flex cursor-pointer list-none items-center gap-1.5 rounded-lg py-1 text-xs font-semibold uppercase tracking-wide text-slate-400 hover:text-slate-300 [&::-webkit-details-marker]:hidden">
        <span className="min-w-0 flex-1 truncate">{group.name}</span>
        <span className="shrink-0 tabular-nums text-slate-600">{group.entries.length}</span>
        <FontAwesomeIcon
          icon={faChevronRight}
          className="size-2.5 shrink-0 text-slate-500 transition-transform group-open/rules-catalogue:rotate-90"
          aria-hidden
        />
      </summary>
      <ul className="mt-0.5 space-y-0.5">
        {group.mission_scope ? (
          <li key={catalogueEntryReactKey(group.mission_scope, 0)}>
            <TaskTypeButton
              entry={group.mission_scope}
              selected={catalogueKey === group.mission_scope.key}
              onSelect={onSelect}
            />
          </li>
        ) : null}
        {group.entries.map((entry, index) => (
          <li key={catalogueEntryReactKey(entry, index)}>
            <TaskTypeButton
              entry={entry}
              selected={catalogueKey === entry.key}
              onSelect={onSelect}
            />
          </li>
        ))}
      </ul>
    </details>
  );
}

export function RulesTaskTypeSidebar({
  groups,
  ungrouped,
  catalogueKey,
  onSelect,
}: RulesTaskTypeSidebarProps) {
  return (
    <aside className="w-56 shrink-0 overflow-y-auto border-r border-white/5 p-3" data-testid="rules-task-type-sidebar">
      <div className="space-y-3">
        {groups.map((group) => (
          <TaskTypeGroup
            key={group.template_id}
            group={group}
            catalogueKey={catalogueKey}
            onSelect={onSelect}
          />
        ))}

        {ungrouped.length > 0 ? (
          <TaskTypeGroup
            group={{ template_id: "standalone", name: "Standalone", entries: ungrouped }}
            catalogueKey={catalogueKey}
            onSelect={onSelect}
            testId="rules-standalone-task-types"
          />
        ) : null}
      </div>
    </aside>
  );
}
